import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarClock,
  Flame,
  PhoneCall,
  Snowflake,
  Sparkles,
  Wind,
} from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 34 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

function Card({ testid, icon: Icon, accent, title, copy, image, className = "", delay = 0 }) {
  const accentText = accent === "ice" ? "text-ice" : "text-forge";
  const hoverBorder =
    accent === "ice"
      ? "hover:border-ice/40 hover:shadow-[0_0_50px_rgba(56,189,248,0.12)]"
      : "hover:border-forge/40 hover:shadow-[0_0_50px_rgba(255,107,0,0.12)]";
  return (
    <motion.article
      {...reveal}
      transition={{ ...reveal.transition, delay }}
      data-testid={testid}
      className={`group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-navy-800/70 transition-[border-color,box-shadow] duration-300 ${hoverBorder} ${className}`}
    >
      {image && (
        <>
          <img
            src={image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25 transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-navy-950/20" />
        </>
      )}
      <div className="relative flex h-full flex-col justify-between p-7 sm:p-8">
        <div className="flex items-start justify-between">
          <span className={`grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/5 ${accentText}`}>
            <Icon className="h-5 w-5" />
          </span>
          <ArrowUpRight className="h-5 w-5 text-slate-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-slate-200" />
        </div>
        <div className="pt-16">
          <h3 className="font-display text-xl font-bold tracking-tight text-slate-50 sm:text-2xl">{title}</h3>
          <p className="mt-2.5 max-w-md text-sm leading-relaxed text-slate-400">{copy}</p>
        </div>
      </div>
    </motion.article>
  );
}

export default function ServicesBento() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...reveal} className="mb-14 max-w-2xl">
          <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-forge">01 — Services</span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Two forces. One perfectly tuned home.
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">
            Every Crowson system is load-calculated, airflow-balanced, and
            commissioned like a machine — because it is one.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <Card
            testid="service-card-ac"
            icon={Snowflake}
            accent="ice"
            title="Ice-Calibrated Cooling"
            copy="Variable-speed inverter AC tuned to your home's exact heat load. Whisper-quiet 52 dB operation, humidity locked to 45%."
            image="https://images.pexels.com/photos/32497161/pexels-photo-32497161.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            className="min-h-[300px] md:col-span-7"
          />
          <Card
            testid="service-card-heating"
            icon={Flame}
            accent="forge"
            title="Forge-Grade Heating"
            copy="98.5% AFUE modulating furnaces and dual-fuel heat pumps rated to -13°F. Even heat, zero cold spots, CO-shield protected."
            className="min-h-[300px] md:col-span-5"
            delay={0.08}
          />
          <Card
            testid="service-card-minisplit"
            icon={Wind}
            accent="ice"
            title="Ductless Mini-Splits"
            copy="Room-by-room zoning with up to 26 SEER2 efficiency. One-day installs, zero ductwork."
            image="https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85"
            className="min-h-[280px] md:col-span-4"
          />
          <Card
            testid="service-card-iaq"
            icon={Sparkles}
            accent="forge"
            title="Indoor Air Quality"
            copy="HEPA-grade filtration, UV purification, and fresh-air exchange. Breathe measurably cleaner air."
            image="https://images.pexels.com/photos/7166935/pexels-photo-7166935.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            className="min-h-[280px] md:col-span-4"
            delay={0.08}
          />
          <Card
            testid="service-card-maintenance"
            icon={CalendarClock}
            accent="ice"
            title="Preventive Maintenance"
            copy="Twice-yearly precision tune-ups. Catch failures before they happen, extend system life by years."
            image="https://images.unsplash.com/photo-1660330589827-da8ab7dd3c02?crop=entropy&cs=srgb&fm=jpg&q=85"
            className="min-h-[280px] md:col-span-4"
            delay={0.16}
          />

          <motion.a
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            data-testid="service-card-emergency"
            href="tel:+16155932628"
            className="group relative overflow-hidden rounded-2xl border border-forge/30 bg-gradient-to-r from-forge/15 via-navy-800 to-ice/10 p-7 transition-colors duration-300 hover:border-forge/60 sm:p-8 md:col-span-12"
          >
            <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
              <div>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-forge">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-forge" />
                  Emergency Dispatch
                </span>
                <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                  System down? A certified tech is rolling today.
                </h3>
              </div>
              <span className="flex shrink-0 items-center gap-3 rounded-full bg-gradient-to-br from-forge to-forge-hi px-6 py-4 font-display text-sm font-bold uppercase tracking-wider text-white shadow-[0_10px_36px_rgba(255,107,0,0.4)] transition-transform duration-200 group-hover:scale-105">
                <PhoneCall className="h-4 w-4" />
                615-593-2628
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
